import React, { useEffect, useState } from "react";
import { eventActions } from "../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import AddressInput from "./InputFields/Addressinput";
import { useNavigate, useParams } from "react-router-dom";
import GoogleMaps from "./GoogleMap";
import Loader from "./Loader";
import PageFrame from "./PageFrame";
import DatePicker from "react-datepicker";
import imageCompression from "browser-image-compression";
import "react-datepicker/dist/react-datepicker.css";
import toast from "react-hot-toast";
import moment from "moment";
import FileUploadField from "../assets/components/FileUploadField";
import PrimaryButton from "../assets/components/PrimaryButton";
import SecondaryButton from "../assets/components/SecondaryButton";

// API dates may be "YYYY-MM-DD" or a full ISO timestamp; keep only the date part as local midnight
const parseEventDate = (value) => {
    if (!value) return null;
    const dateOnly = String(value).slice(0, 10);
    const parsed = new Date(`${dateOnly}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const CreateEvent = ({ setAddStore }) => {
    const params = useParams();

    const noqUser = localStorage.getItem("noqEventUser") || sessionStorage.getItem("noqEventUser");
    const [user] = useState(noqUser);
    const userObj = JSON.parse(user);

    const [uploadingImg, setUploadingImg] = useState(false);
    const [logoPreview, setLogoPreview] = useState(null);
    const [address, setAddress] = useState({});
    const [latLng, setLatLng] = useState({
        lat: "",
        lng: "",
    });
    const [organizers, setOrganizers] = useState([]);
    const [isCentering, setIsCentering] = useState(false);
    const [eventData, setEventData] = useState({
        name: "",
        services: "",
    });

    const { eventId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const {
        isFetchedSingleEventSuccess,
        isFetchingSingleEvent,
        isFetchedOrganizersSuccess,
        isCreatingEvent,
        isCreatedEventSuccess,
        isUpdatingEvent,
        isUpdatedEventSuccess,
    } = useSelector((state) => state.eventManager);

    const handleChange = (name, value) => {
        setEventData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleLogoUpload = async (selectedFile) => {
        if (!selectedFile) return false;

        if (!["image/jpeg", "image/png", "image/webp"].includes(selectedFile.type)) {
            toast.error("Please upload a PNG, JPG, or WEBP image.");
            return false;
        }

        setUploadingImg(true);
        try {
            let file = selectedFile;

            if (file.size > 1024 * 1024) {
                file = await imageCompression(file, {
                    maxSizeMB: 1,
                    maxWidthOrHeight: 1200,
                    useWebWorker: true,
                });
            }

            if (file.size > 1024 * 1024) {
                toast.error("File size must be less than 1MB");
                return false;
            }

            setLogoPreview(null);
            setEventData((prev) => ({ ...prev, logo: file }));
            return file;
        } catch (error) {
            console.error("Unable to process event logo:", error);
            toast.error("Unable to process this image. Please try another file.");
            return false;
        } finally {
            setUploadingImg(false);
        }
    };

    const handleRemoveLogo = () => {
        setLogoPreview(null);
        handleChange("logo", null); // Remove the file from state
    };

    const handleBack = () => {
        if (eventId) {
            navigate(`/dashboard/event/${eventId}`);
            return;
        }

        setAddStore?.(false);
        setEventData({ name: "", services: "" });
        setLogoPreview(null);
        setAddress({});
    };

    const handleGetAddress = async (lat, lng) => {
        if (!lat && !lng) return;
        try {
            const response = await fetch(
                `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${process.env.REACT_APP_GOOGLE_MAP_API}`
            );
            const addressData = await response.json();

            if (addressData?.results?.length > 0) {
                const place = addressData.results[0];
                const components = place.address_components;

                const getComponent = (type, short = false) =>
                    components.find((c) => c.types.includes(type))?.[
                        short ? "short_name" : "long_name"
                    ] || "";

                const formattedData = {
                    description: place.formatted_address,
                    lat,
                    lng,
                    formattedAddress: place.formatted_address,
                    city: getComponent("locality"), // e.g. Calgary
                    province: {
                        long: getComponent("administrative_area_level_1"), // e.g. Alberta
                        short: getComponent("administrative_area_level_1", true), // e.g. AB
                    },
                    country: {
                        long: getComponent("country"),
                        short: getComponent("country", true), // e.g. CA
                    },
                    postalCode: getComponent("postal_code"),
                };

                setAddress((prev) => ({
                    ...prev,
                    ...formattedData,
                }));

                setEventData((prev) => ({
                    ...prev,
                    province: formattedData?.province?.short || prev?.province,
                }));
            }
        } catch (error) {
            console.error("Error fetching address:", error);
        }
    };

    const recenter = async () => {
        setIsCentering(true);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                setLatLng({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                });
                setIsCentering(false);
            },
            (error) => {
                setIsCentering(false);
                console.error("Error getting user location:", error);
            },
            {
                enableHighAccuracy: true,
                timeout: 10000, // 10 seconds
                maximumAge: 0, // Do not use a cached position
            }
        );
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const formData = new FormData();

        formData.append("name", eventData.name);
        formData.append("description", "");
        const formattedStartDate = eventData.startDate
            ? moment(eventData.startDate).format("YYYY-MM-DD")
            : "";
        const formattedEndDate = eventData.endDate
            ? moment(eventData.endDate).format("YYYY-MM-DD")
            : "";
        const country = address?.country?.long || address?.country || "";
        const postalCode = address?.postalCode || address?.postal_code || "";

        formData.append("street", address?.description || address?.street || "");
        formData.append("city", address?.city || "");
        formData.append("postal_code", postalCode);
        formData.append("country", country);
        formData.append("start_date", formattedStartDate);
        formData.append("end_date", formattedEndDate);
        // formData.append("logo", eventData?.logo);
        formData.append("latitude", address?.lat ?? "");
        formData.append("longitude", address?.lng ?? "");

        // Compressed images come back as a Blob, so pass a filename for the multipart upload
        if (eventData.logo instanceof Blob) {
            formData.append("logo", eventData.logo, eventData.logo.name || "event-logo.jpg");
        }

        if (!eventId) {
            dispatch(
                eventActions.createEvent(
                    organizers?.[0]?.id,
                    formData,
                    userObj?.token
                )
            );
        } else {
            dispatch(
                eventActions.updateEvent(
                    eventId,
                    // {
                    //     name: eventData.name,
                    //     description: "",
                    //     street: address?.description,
                    //     city: address?.city,
                    //     postal_code: address?.postal_code,
                    //     country: address?.country,
                    //     start_date: eventData.startDate,
                    //     end_date: eventData.endDate,
                    //     logo: eventData?.logo,
                    // },
                    formData,
                    userObj?.token
                )
            );
        }
    };

    useEffect(() => {
        handleGetAddress(latLng?.lat, latLng?.lng);
    }, [latLng?.lat, latLng?.lng]);

    useEffect(() => {
        if (!isFetchedOrganizersSuccess) {
            dispatch(eventActions.getOrganizers());
        } else {
            setOrganizers(isFetchedOrganizersSuccess);
        }
    }, [dispatch, isFetchedOrganizersSuccess]);

    useEffect(() => {
        if (eventId) {
            dispatch(eventActions.getSingleEvent(eventId));
        }
    }, [dispatch, eventId]);

    useEffect(() => {
        if (isFetchedSingleEventSuccess && eventId) {
            // setEventDetails(isFetchedSingleEventSuccess);s
            setEventData({
                name: isFetchedSingleEventSuccess?.name,
                startDate: parseEventDate(isFetchedSingleEventSuccess?.start_date),
                endDate: parseEventDate(isFetchedSingleEventSuccess?.end_date),
                logo: isFetchedSingleEventSuccess?.logo_url,
            });
            setLogoPreview(isFetchedSingleEventSuccess?.logo_url || null);
            setAddress({
                description: isFetchedSingleEventSuccess?.street || "",
                street: isFetchedSingleEventSuccess?.street || "",
                city: isFetchedSingleEventSuccess?.city || "",
                postalCode: isFetchedSingleEventSuccess?.postal_code || "",
                country: isFetchedSingleEventSuccess?.country || "",
                lat: Number(isFetchedSingleEventSuccess?.latitude) || "",
                lng: Number(isFetchedSingleEventSuccess?.longitude) || "",
            });

            handleGetAddress(
                Number(isFetchedSingleEventSuccess?.latitude),
                Number(isFetchedSingleEventSuccess?.longitude)
            );
        }
    }, [isFetchedSingleEventSuccess, eventId]);

    useEffect(() => {
        if (isCreatedEventSuccess) {
            toast.success("Event created successfully");
            navigate(`/dashboard/event/${isCreatedEventSuccess?.id}`);
            setEventData({ name: "", services: "" });
            setAddress({});
            dispatch(eventActions.resetCreateEventSuccess());
        }
        if (isUpdatedEventSuccess) {
            toast.success("Event updated successfully");
            navigate(`/dashboard/event/${isUpdatedEventSuccess?.id || eventId}`);
            setEventData({ name: "", services: "" });
            setAddress({});
            dispatch(eventActions.resetUpdateEventSuccess());
        }
    }, [isCreatedEventSuccess, isUpdatedEventSuccess, dispatch, navigate]);
    return (
        <div className="create-event-root font-sans min-h-screen w-full">
            <div className={eventId ? "edit-event-shell" : "hidden"}>
                <PageFrame
                    isLoading={isFetchingSingleEvent}
                    frameLinks={[
                        // { text: "Dashboard", url: `/dashboard`, isActive: false },
                        {
                            text: "Event Details",
                            url: `/dashboard/event/${params?.eventId}`,
                            isActive: true,
                        },
                        {
                            text: "Vendors & invitations",
                            url: `/dashboard/event/${params?.eventId}/invitations`,
                            isActive: false,
                        },
                        {
                            text: "Event insights",
                            url: `/dashboard/event/${params?.eventId}/insights`,
                            isActive: false,
                        },
                    ]}
                    childrenContainerClass="edit-event-main"
                >
                    <div className="edit-event-page">
                        <div className="edit-event-card">
                            <div className="edit-event-header flex flex-col mb-6">
                                <div className="flex flex-row relative items-center justify-center gap-5">
                                    <p className="text-xl font-bold">Edit event</p>
                                </div>

                                <p className="text-base text-grey-subtext">Update the event details guests and vendors rely on.</p>
                            </div>
                            <div className="w-full flex  justify-center m-auto max-sm:w-full">
                                <form onSubmit={handleSubmit} className="create-event-form edit-event-form w-full">
                                    <div className="mb-4">
                                        <p className="text-base mb-1 text-grey-subtext font-semibold">
                                            Event Name
                                        </p>
                                        <input
                                            type={"text"}
                                            placeholder="Enter your event name"
                                            className="input-field"
                                            required
                                            name="name"
                                            value={eventData.name}
                                            onChange={(e) =>
                                                handleChange(e.target.name, e.target.value)
                                            }
                                        />
                                    </div>
                                    <div className="mb-4">
                                        <p className="text-base mb-1 text-grey-subtext font-semibold">
                                            Event Location/Address
                                        </p>

                                        <AddressInput
                                            setData={setAddress}
                                            savedAddress={address}
                                            placeholder="Event address"
                                            required={true}
                                            name={"storeAddress"}
                                        />
                                    </div>

                                    {address?.description && (
                                        <div className="mb-4">
                                            <div className="flex items-center justify-between mb-2">
                                                <p className="text-base mb-1 text-grey-subtext font-semibold">
                                                    Pin exact location
                                                </p>

                                                <button
                                                    onClick={recenter}
                                                    type="button"
                                                    className="py-2 px-3 bg-gray-300 hover:bg-gray-200 rounded-md text-black flex space-x-2.5 items-center"
                                                >
                                                    <i className="ri-map-pin-range-line"></i>{" "}
                                                    <span className="text-sm font-semibold">
                                                        Use my current position
                                                    </span>
                                                    {isCentering && (
                                                        <Loader size={4} color={"white"} />
                                                    )}
                                                </button>
                                            </div>

                                            <div className="flex">
                                                {/* <Map lat={address?.lat} lng={address?.lng} /> */}
                                                <GoogleMaps
                                                    lat={address?.lat}
                                                    lng={address?.lng}
                                                    setLatLng={setLatLng}
                                                />
                                            </div>
                                        </div>
                                    )}
                                    <div className="edit-event-date-grid mb-4 flex flex-row gap-5 w-full">
                                        {/* Start Date Picker */}
                                        <div className="w-1/2">
                                            <p className="text-base mb-1 text-grey-subtext font-semibold">
                                                Start Date
                                            </p>
                                            <DatePicker
                                                selected={eventData.startDate}
                                                onChange={(date) => handleChange("startDate", date)}
                                                className="input-field"
                                                placeholderText="Select Start Date"
                                                dateFormat="yyyy-MM-dd"
                                                wrapperClassName="w-full"
                                                minDate={eventId ? undefined : new Date()}
                                                maxDate={eventData.endDate} // Prevent selecting a start date after the end date
                                            />
                                        </div>

                                        {/* End Date Picker */}
                                        <div className="w-1/2">
                                            <p className="text-base mb-1 text-grey-subtext font-semibold">
                                                End Date
                                            </p>
                                            <DatePicker
                                                selected={eventData.endDate}
                                                onChange={(date) => handleChange("endDate", date)}
                                                className="input-field"
                                                placeholderText="Select End Date"
                                                dateFormat="yyyy-MM-dd"
                                                wrapperClassName="w-full"
                                                minDate={eventData.startDate} // Prevent selecting an end date before the start date
                                                disabled={!eventData.startDate} // Disable until a start date is chosen
                                            />
                                        </div>
                                    </div>
                                    <FileUploadField
                                        label="Upload Event Logo"
                                        name="logo"
                                        accept="image/png,image/jpeg,image/webp"
                                        value={logoPreview}
                                        loading={uploadingImg}
                                        disabled={isUpdatingEvent}
                                        onFileSelect={handleLogoUpload}
                                        onRemove={handleRemoveLogo}
                                    />
                                    <div className="create-event-actions">
                                        <SecondaryButton
                                            className="create-event-action-secondary"
                                            onClick={handleBack}
                                            disabled={isUpdatingEvent || uploadingImg}
                                        >
                                            Cancel
                                        </SecondaryButton>
                                        <PrimaryButton
                                            type="submit"
                                            className="create-event-action-primary"
                                            disabled={isCreatingEvent || isUpdatingEvent || uploadingImg}
                                        >
                                            {isCreatingEvent
                                                ? "Creating Event..."
                                                : isUpdatingEvent
                                                  ? "Updating Event..."
                                                  : "Save changes"}
                                        </PrimaryButton>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </PageFrame>
            </div>

            <div
                className={
                    eventId
                        ? "hidden"
                        : "create-event-standalone flex justify-center m-auto w-full min-h-screen py-10"
                }
            >
                <div className="w-full max-sm:mt-10  px-10 ">
                    <div className="flex flex-col  text-center mb-6 ">
                        <div className="flex flex-row relative items-center justify-center gap-5">
                            <span
                                className="material-icons-outlined cursor-pointer absolute left-0"
                                onClick={handleBack}
                            >
                                keyboard_backspace
                            </span>
                            <p className="text-xl font-bold">
                                {eventId ? "Edit Event" : "Add New Event"}
                            </p>
                        </div>

                        <p className={eventId ? "text-base text-grey-subtext" : ""}>
                            {eventId ? "Edit" : "Enter"} name and details of your Event.
                        </p>
                    </div>
                    <div className="w-full flex  justify-center m-auto max-sm:w-full">
                        <form onSubmit={handleSubmit} className="create-event-form w-full">
                            <div className="mb-4">
                                <p className="text-base mb-1 text-grey-subtext font-semibold">
                                    Event Name
                                </p>
                                <input
                                    type={"text"}
                                    placeholder="Enter your event name"
                                    className="input-field"
                                    required
                                    name="name"
                                    value={eventData.name}
                                    onChange={(e) => handleChange(e.target.name, e.target.value)}
                                />
                            </div>
                            <div className="mb-4">
                                <p className="text-base mb-1 text-grey-subtext font-semibold">
                                    Event Location/Address
                                </p>

                                <AddressInput
                                    setData={setAddress}
                                    savedAddress={address}
                                    placeholder="Event address"
                                    required={true}
                                    name={"storeAddress"}
                                />
                            </div>

                            {address?.description && (
                                <div className="mb-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-base mb-1 text-grey-subtext font-semibold">
                                            Pin exact location
                                        </p>

                                        <button
                                            onClick={recenter}
                                            type="button"
                                            className="py-2 px-3 bg-gray-300 hover:bg-gray-200 rounded-md text-black flex space-x-2.5 items-center"
                                        >
                                            <i className="ri-map-pin-range-line"></i>{" "}
                                            <span className="text-sm font-semibold">
                                                Use my current position
                                            </span>
                                            {isCentering && <Loader size={4} color={"white"} />}
                                        </button>
                                    </div>

                                    <div className="flex">
                                        {/* <Map lat={address?.lat} lng={address?.lng} /> */}
                                        <GoogleMaps
                                            lat={address?.lat}
                                            lng={address?.lng}
                                            setLatLng={setLatLng}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="mb-4 flex flex-row gap-5 w-full">
                                {/* Start Date Picker */}
                                <div className="w-1/2">
                                    <p className="text-base mb-1 text-grey-subtext font-semibold">
                                        Start Date
                                    </p>
                                    <DatePicker
                                        selected={eventData.startDate}
                                        onChange={(date) => handleChange("startDate", date)}
                                        className="input-field w-full"
                                        placeholderText="Select Start Date"
                                        dateFormat="yyyy-MM-dd"
                                        wrapperClassName="w-full"
                                        minDate={new Date()} // Prevent selecting dates before today
                                        maxDate={eventData.endDate} // Prevent selecting a start date after the end date
                                    />
                                </div>

                                {/* End Date Picker */}
                                <div className="w-1/2">
                                    <p className="text-base mb-1 text-grey-subtext font-semibold">
                                        End Date
                                    </p>
                                    <DatePicker
                                        selected={eventData.endDate}
                                        onChange={(date) => handleChange("endDate", date)}
                                        className="input-field w-full"
                                        placeholderText="Select End Date"
                                        dateFormat="yyyy-MM-dd"
                                        wrapperClassName="w-full"
                                        minDate={eventData.startDate} // Prevent selecting an end date before the start date
                                        disabled={!eventData.startDate} // Disable until a start date is chosen
                                    />
                                </div>
                            </div>
                            {/*
                            <div className="mb-4 flex flex-col w-full">
                                <p className="text-base mb-1 text-grey-subtext font-semibold">
                                    Invite Vendor via Email
                                </p>

                                <CreatableSelect
                                    isMulti
                                    options={[]}
                                    value={
                                        eventData?.services
                                            ? eventData?.services?.split(",")?.map((item) => ({
                                                label: item,
                                                value: item,
                                            }))
                                            : []
                                    }
                                    required
                                    name="vendor emails"
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            border: "1px solid #000000",
                                            outline: "#5386db",
                                            borderRadius: "5px",
                                            minHeight: "65px",
                                            width: "100%",
                                            boxSizing: "border-box",
                                            backgroundColor: "transparent",
                                            padding: "0 12px",
                                        }),
                                    }}
                                    setValue={(v, a) => console.log("firstv", v)}
                                    onChange={(values) => {
                                        handleChange(
                                            "services",
                                            values?.map((services) => services?.value)?.join(",")
                                        );
                                    }}
                                />
                            </div> */}

                            <FileUploadField
                                label="Upload Event Logo"
                                name="logo"
                                accept="image/png,image/jpeg,image/webp"
                                value={logoPreview}
                                loading={uploadingImg}
                                disabled={isCreatingEvent}
                                onFileSelect={handleLogoUpload}
                                onRemove={handleRemoveLogo}
                            />

                            <div className="create-event-actions">
                                <SecondaryButton
                                    className="create-event-action-secondary"
                                    onClick={handleBack}
                                    disabled={isCreatingEvent || uploadingImg}
                                >
                                    Back
                                </SecondaryButton>
                                <PrimaryButton
                                    type="submit"
                                    className="create-event-action-primary"
                                    disabled={isCreatingEvent || uploadingImg}
                                >
                                    {isCreatingEvent
                                        ? "Creating Event..."
                                        : isUpdatingEvent
                                          ? "Updating Event..."
                                          : "Save"}
                                </PrimaryButton>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateEvent;
